import { useState } from 'react';
import { useCreateAppointment, useUpdateAppointment, isConflictError } from '@/hooks/useAppointments';
import { appointmentsApi, type ConflictErrorResponse } from '@/lib/api';
import { toast } from 'sonner';
import type { BookingFormData } from '@geely/shared';
import type { BookingStep, BookingSubStep } from './useBookingWizard';

interface UseBookingSubmitOptions {
  selectedStoreId: string;
  selectedDate: string;
  selectedTime: string;
  getFormValues: () => BookingFormData;
  isRescheduling: boolean;
  originalAppointmentId: string | null;
  setStep: (step: BookingStep) => void;
  setSubStep: (subStep: BookingSubStep) => void;
  setSelectedTime: (time: string) => void;
  setAppointmentId: (id: string) => void;
}

export function useBookingSubmit(options: UseBookingSubmitOptions) {
  const {
    selectedStoreId,
    selectedDate,
    selectedTime,
    getFormValues,
    isRescheduling,
    originalAppointmentId,
    setStep,
    setSubStep,
    setSelectedTime,
    setAppointmentId,
  } = options;

  const createAppointmentMutation = useCreateAppointment();
  const updateAppointmentMutation = useUpdateAppointment();

  const [conflictError, setConflictError] = useState<ConflictErrorResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const redirectToDateTimeSelection = () => {
    setStep(1);
    setSubStep(4);
    setSelectedTime('');
  };

  // CAMADA 3: JIT (Just-In-Time) validation - verificar disponibilidade imediatamente antes de criar
  const validateAvailability = async (): Promise<boolean> => {
    const availabilityCheck = await appointmentsApi.checkAvailability(
      selectedStoreId,
      selectedDate,
      selectedTime
    );

    if (!availabilityCheck.available) {
      toast.error('Este horário não está mais disponível. Por favor, escolha outro horário.');
      redirectToDateTimeSelection();

      return false;
    }

    return true;
  };

  const saveAppointment = async (customerData: BookingFormData) => {
    const appointmentData = {
      storeId: selectedStoreId,
      date: selectedDate,
      time: selectedTime,
      ...customerData,
      vehicleVin: customerData.vehicleVin || null,
    };

    if (isRescheduling && originalAppointmentId) {
      await updateAppointmentMutation.mutateAsync({
        id: originalAppointmentId,
        data: appointmentData,
      });
      setAppointmentId(originalAppointmentId);

      toast.success('Agendamento remarcado com sucesso!');
    } else {
      const newAppointment = await createAppointmentMutation.mutateAsync(appointmentData);
      setAppointmentId(newAppointment.id);

      toast.success('Agendamento criado com sucesso!');
    }
  };

  // CAMADA 4: Handle 409 Conflict with alternative slots
  const handleConflictError = (error: any) => {
    if (isConflictError(error)) {
      const conflictData = error.response?.data as ConflictErrorResponse;
      setConflictError(conflictData);

      toast.error('Este horário foi reservado por outra pessoa. Veja os horários alternativos disponíveis.');

      redirectToDateTimeSelection();
    } else {
      toast.error(
        isRescheduling
          ? 'Erro ao remarcar agendamento. Tente novamente.'
          : 'Erro ao criar agendamento. Tente novamente.'
      );

      console.error('Error saving appointment:', error);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setConflictError(null);

    try {
      const isAvailable = await validateAvailability();
      if (!isAvailable) return;

      await saveAppointment(getFormValues());
      setStep(5);
    } catch (error: any) {
      handleConflictError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyId = (appointmentId: string) => {
    const fallbackCopy = () => {
      const textarea = document.createElement('textarea');
      textarea.value = appointmentId;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(appointmentId).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }

    setCopied(true);
    toast.success('ID copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const clearConflictError = () => {
    setConflictError(null);
  };

  return {
    conflictError,
    isSubmitting,
    copied,
    handleSubmit,
    handleCopyId,
    clearConflictError,
  };
}
